import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { Row, Col, Card, Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';

// External libraries
import moment from 'moment';
import ClipLoader from 'react-spinners/ClipLoader';
import CsLineIcons from 'cs-line-icons/CsLineIcons';

// utils
import { handleAPIError, handleCatchError } from 'utils/handleErrors';
import formatMoney from 'utils/formatMoney';
// components
import { ToastErrorCath, ToastSuccess } from '../../../../components/alerts/toast';
import ButtonRequest from '../../../../components/buttons/ButtonRequest';
// api
import { toggleCancellationState, returnShipping, penaltyShipping } from '../../../api/Api-admin';

const ShippingCard = ({ data = {}, setTrigger = () => {} }) => {
  // console.log('Shipping refunded data:', data);
  const [isModalActive, setIsModalActive] = useState(false);
  const [isModalStatesActive, setIsModalStatesActive] = useState({ isOpen: false, state: '' });
  const [isDetailModalActive, setIsDetailModalActive] = useState(false);

  const [isChangeStateLoader, setIsChangeStateLoader] = useState(false);
  const [isAddChargerLoader, setIsAddChargerLoader] = useState(false);

  const [canceled, setCanceled] = useState(data.canceled);
  const [consumed, setConsumed] = useState(data.consumed);
  const [refunded, setRefunded] = useState(data.refunded);
  const [requested, setRequested] = useState(data.requested);
  const [notApply, setNotApply] = useState(data.not_apply);
  // to control OverlayTrigger in status cancelled, refunded, consumed
  const canceledRef = useRef();
  const usedRef = useRef();
  const refundedRef = useRef();
  const requestedRef = useRef();
  const notApplyRef = useRef();

  // Sincronizar estados cuando `data` cambie
  useEffect(() => {
    setCanceled(data.canceled);
    setConsumed(data.consumed);
    setRefunded(data.refunded);
    setRequested(data.requested);
    setNotApply(data.not_apply);
  }, [data]);
  const cleanOvertrigger = () => {
    // Esperar un poco para que el modal cierre y luego quitar el foco
    setTimeout(() => {
      canceledRef.current?.blur();
      usedRef.current?.blur();
      refundedRef.current?.blur();
      requestedRef.current?.blur();
      setNotApply.current?.blur();
    }, 100);
  };
  const closeStatusModal = () => {
    setIsModalStatesActive({ isOpen: false, state: '' });
    cleanOvertrigger();
  };
  const closeModal = () => {
    setIsModalActive(false);
    cleanOvertrigger();
  };
  const handlerCancellationState = async (state) => {
    try {
      setIsChangeStateLoader(true);
      const response = await toggleCancellationState(data.shipping_id, state);
      // console.log('toggleCancellationState res:', response);
      if (response?.success !== undefined) {
        if (state === 'canceled') setCanceled(!canceled);
        if (state === 'consumed') setConsumed(!consumed);
        if (state === 'requested') setRequested(!requested);
        if (state === 'not_apply') setNotApply(!notApply);
        setTrigger((prev) => !prev);
      } else if (response.error) {
        handleAPIError(response);
      }
    } catch (error) {
      handleCatchError(error);
    } finally {
      setIsModalStatesActive({ isOpen: false, state: '' });
      setIsChangeStateLoader(false);
    }
  };
  const handlerReturnShipping = async () => {
    try {
      setIsAddChargerLoader(true);
      const response = await returnShipping(data.shipping_id);
      // console.log('return shipping response:', response);
      if (response.shipping_refunded !== undefined) {
        ToastSuccess(`⭐ ${response.charge_statement}. Cargo de $${response.amount} aplicado al envío ${response.reference}.`);
        setRefunded(true);
        setTrigger((prev) => !prev);
      }
      if (response.error) {
        handleAPIError(response);
      }
    } catch (error) {
      handleCatchError(error);
    } finally {
      closeModal();
      setIsAddChargerLoader(false);
    }
  };
  const handlerPenaltyShipping = async () => {
    try {
      setIsAddChargerLoader(true);
      const response = await penaltyShipping(data.shipping_id);
      // console.log('penalty shipping response:', response);
      if (response.shipping_penalty) {
        ToastSuccess(`⭐ ${response.charge_statement}. Cargo de $${response.amount} aplicado al envío ${response.reference}.`);
        setRefunded(true);
        setTrigger((prev) => !prev);
      }
      if (response.error) {
        handleAPIError(response);
      }
    } catch (error) {
      handleCatchError(error);
    } finally {
      closeModal();
      setIsAddChargerLoader(false);
    }
  };
  const setStatusToUpdate = (state) => {
    const infoState = {
      not_apply: {
        title: 'No Aplica',
        description: 'No es posible reembolsar: la guía fue utilizada por el cliente previamente.',
        action: notApply ? 'remover' : 'agregar',
      },
      // consumed: { title: 'Utilizada', description: 'La guía ya fue utilizada por el cliente.', action: consumed ? 'remover' : 'agregar' },
      requested: { title: 'Solicitada', description: 'La cancelación (reembolso) ya fue solicitada al proveedor.', action: requested ? 'remover' : 'agregar' },
      canceled: {
        title: 'Cancelada',
        description: 'La cancelación fue aceptada y el proveedor ya reembolsó a Autopaquete.',
        action: canceled ? 'remover' : 'agregar',
      },
    };
    return infoState[state];
  };
  const statusToUpdate = setStatusToUpdate(isModalStatesActive.state);
  return (
    <>
      {/* Refunded Modal Start */}
      <Modal show={isModalActive} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title className="text-primary">
            <div className="text-primary">Modificar saldo del usuario</div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-infow">
          <div className="text-semi-large mb-3">
            ¿Confirmas que deseas <strong className="px-1">{data.refunded ? 'volver a cobrar' : 'reembolsar'}</strong>este envío?
          </div>
          <div className="text-alternate">{data.legal_name}</div>
          <div className="text-alternate">{`${data?.shipping_code}`}</div>
          <div className="text-alternate">{`ID: ${data?.shipping_id}`}</div>
          <div className="text-alternate">
            <span>COSTO:</span>
            <strong className={`${data?.refunded ? 'text-danger' : 'text-success'}`}> {`$${formatMoney(data.total)}`}</strong>
          </div>
          {!data?.refunded && <span className="text-medium text-muted">* El costo del seguro no será reembolsado</span>}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex flex-wrap justify-content-end bg-warningd">
            <Button variant="outline-primary" style={{ height: '36px' }} className="w-auto me-2 mb-2" onClick={closeModal}>
              Cancelar
            </Button>
            <ButtonRequest
              title="Si, continuar"
              titleLoading="Cargando"
              handlerFn={data?.refunded ? handlerPenaltyShipping : handlerReturnShipping}
              isButtonDisabled={isAddChargerLoader}
              isLoading={isAddChargerLoader}
            />
          </div>
        </Modal.Footer>
      </Modal>
      {/* Refunded Modal End */}
      {/* Cancelled request of cancellation Start */}
      <Modal show={isModalStatesActive.isOpen} onHide={closeStatusModal}>
        <Modal.Header closeButton>
          <Modal.Title className="text-primary">
            {' '}
            <div className="text-primary">Estado de solicitud </div>{' '}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-infow">
          <div className="text-semi-large mb-4">
            ¿Confirmas que deseas <strong>{statusToUpdate?.action}</strong> este estado a la solicitud de la guía <strong>{data?.shipping_id}</strong>?
          </div>
          <h1 className="text-semi-large text-primary fw-bold ">
            <strong>{`"${statusToUpdate?.title}"`}</strong>
          </h1>
          <div className="text-alternate">{statusToUpdate?.description}</div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex flex-wrap justify-content-end bg-warningd">
            <Button variant="outline-primary" style={{ height: '36px' }} className="w-auto me-2 mb-2" onClick={closeStatusModal}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="xxl"
              // className="mx-2"
              className="py-0"
              onClick={() => handlerCancellationState(isModalStatesActive.state)}
              style={{ height: '36px' }}
            >
              {isChangeStateLoader && <ClipLoader disabled={isChangeStateLoader} color="#FFFFFF" loading={isChangeStateLoader} size={10} />}
              {isChangeStateLoader ? 'Cargando' : 'Si, continuar'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      {/* Cancelled request of cancellation End */}
      {/* Detail Modal Start */}
      <Modal
        className="modal-right"
        show={isDetailModalActive}
        onHide={() => {
          setIsDetailModalActive(false);
          cleanOvertrigger();
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Detalle de solicitud</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-infow">
          <p className="text-small text-muted mb-1">USUARIO</p>
          <div className="text-primary">{data.legal_name}</div>
          <div className="text-alternate">{`ID: ${data?.api_user}`}</div>
          <p className="text-small text-muted mt-4 mb-1">ENVÍO</p>
          <div className="text-primary">{`${data?.shipping_code}`}</div>
          <div className="text-alternate">{`ID: ${data?.shipping_id}`}</div>
          <div className="text-alternate">
            <span>Costo:</span>
            <strong> {`$${formatMoney(data?.total)}`}</strong>
          </div>
          <div className="text-alternate">{`Proveedor: ${data?.provider}`}</div>
          <p className="text-small text-muted mt-4 mb-1">SOLICITUD DE REEMBOLSO</p>
          <div className="text-primary">
            <span> {`${data?.reason.toUpperCase()}`}</span>
          </div>
          <div className="text-alternate">{`Fecha: ${moment(data?.created_date).format('DD-MM-YYYY')} `}</div>
          <div className="text-alternate">{`Hora: ${data?.created_date.slice(11, 16)}`}</div>
          <p className="text-small text-muted mt-4 mb-2">RESOLUCIÓN DEL LA SOLICITUD</p>
          {!data?.consumed && !data?.refunded && !data?.not_apply && <div className="text-alternate">Pendiente de Resolución</div>}
          {data?.consumed && (
            <>
              <div className="text-primary">PENALIZACIÓN</div>
              <div className="text-alternate">
                La guía fue utilizada después de haber sido reembolsada, por lo que se aplicó un cargo adicional:{' '}
                <strong>"Cargo por uso de guía cancelada"</strong>.
              </div>
            </>
          )}
          {data?.refunded && (
            <>
              <div className="text-primary">APROBADA</div>
              <div className="text-alternate">{`Autor: ${data?.author || 'No disponible'}`}</div>
              <div className="text-alternate">
                <span>Monto:</span>
                {data?.refunded_amount > 0 ? (
                  <strong className="text-success"> {`$${formatMoney(data?.refunded_amount)}`}</strong>
                ) : (
                  <span className="text-alternate"> No disponible</span>
                )}
              </div>
            </>
          )}
          {data?.not_apply && (
            <>
              <div className="text-primary">RECHAZADA</div>
              <div className="text-alternate">La guía ya fue utilizada. No se puede procesar el reembolso.</div>
            </>
          )}
          <p className="text-small text-muted mt-4 mb-2">FECHA DE ULTIMA ACTUALIZACIÓN</p>
          <div className="text-alternate">{`Fecha: ${moment(data?.updated_date).format('DD-MM-YYYY') || 'No disponible'}`}</div>
          <div className="text-alternate">{`Hora: ${data?.updated_date.slice(11, 16)}`}</div>
          {/* {data?.consumed && data?.refunded && (
            <div className="text-alternate text-small mt-2">
              Si la guía se usó después del reembolso, debes cobrar el cargo adicional: “Cargo por uso de guía cancelada”.
            </div>
          )} */}
        </Modal.Body>
      </Modal>
      {/* Detail Modal End */}
      <Card className="mb-2 py-4 py-xxl-0 ps-5 pe-4 h-100 text-alternate">
        <Row className="g-0 h-100 sh-xxl-9 position-relative overflow-y-hidden">
          <Col xs="4" sm="2" xxl="1" className="d-flex flex-column mb-xxl-0 mb-3 justify-content-center order-3 order-sm-4 order-xxl-1 bg-infox">
            <div className="text-muted text-small d-xxl-none">Crm Id</div>
            <OverlayTrigger delay={{ show: 200, hide: 0 }} placement="top" overlay={<Tooltip id="tooltip-top">Ver perfil de usuario</Tooltip>}>
              <NavLink className="anchor-effect-primary" to={`/users-all/detail/${data?.api_user}`}>
                {data.api_user}
              </NavLink>
            </OverlayTrigger>
          </Col>
          <Col
            xs="12"
            sm="10"
            xxl="2"
            className="d-flex flex-column pe-1 pe-xxl-2 mb-3 mb-xxl-0 justify-content-center order-2 order-sm-1 order-xxl-2 bg-successx"
          >
            <div className="text-muted text-small d-xxl-none">Servicio / ID Envío </div>
            <OverlayTrigger delay={{ show: 200, hide: 0 }} placement="top" overlay={<Tooltip id="tooltip-top">Ver detalle de envío</Tooltip>}>
              <NavLink className="anchor-effect-primary" to={`/shipping-all/detail/${data?.shipping_id}/${data?.legal_name}/${data?.api_user}`}>
                {data?.shipping_code}
              </NavLink>
            </OverlayTrigger>
            <div className="text-medium text-muted text-truncate">{data?.shipping_id}</div>
          </Col>
          <Col
            xs="9"
            sm="8"
            xxl="1"
            className="d-flex flex-column pe-1 mb-3 mb-xxl-0 justify-content-center align-items-xxl-center order-4 order-sm-3 bg-warningx"
          >
            <div className="text-muted text-small d-xxl-none">Proveedor / Fecha</div>
            <OverlayTrigger
              delay={{ show: 200, hide: 0 }}
              placement="top"
              overlay={<Tooltip id="tooltip-top">{data?.reason ? data?.reason.toUpperCase() : ''}</Tooltip>}
            >
              <div className="text-alternate cursor-pointer">{data?.provider}</div>
            </OverlayTrigger>
            <div className="text-medium text-muted text-truncate">{moment(data?.created_date).format('DD-MM-YYYY')}</div>
          </Col>
          <Col
            xs="3"
            sm="2"
            xxl="2"
            className="d-flex flex-column pe-1 mb-3 mb-xxl-0 justify-content-center align-items-start align-items-xxl-center order-4 order-sm-5 bg-secondaryx"
          >
            <div className="text-muted text-small d-xxl-none">Costo</div>
            <div className="d-flex align-items-center">
              <span className="text-small text-muted">$</span>
              <div className="text-alternate">{formatMoney(data.total)}</div>
            </div>
          </Col>
          <Col
            xs="6"
            sm="auto"
            xxl="1"
            className="pe-1 mb-2 mb-xxl-0 d-flex flex-column justify-content-center align-items-endx align-items-sm-start text-endx order-5 bg-successx"
          >
            <OverlayTrigger
              delay={{ show: 200, hide: 0 }}
              placement="top"
              overlay={<Tooltip id="tooltip-top">El reembolso ya fue solicitado al proveedor.</Tooltip>}
            >
              <div className="text-muted text-small" ref={requestedRef}>
                Solicitada
              </div>
            </OverlayTrigger>
            <input
              type="checkbox"
              className="form-check-input cursor-pointer"
              name="receiver"
              checked={requested}
              onClick={() => {
                setIsModalStatesActive({ isOpen: true, state: 'requested' });
              }}
            />
          </Col>
          <Col
            xs="6"
            sm="auto"
            xxl="1"
            className="ps-4 mb-2 mb-xxl-0 me-0 me-xxl-0 d-flex flex-column justify-content-center align-items-endx align-items-sm-start text-endx order-5 bg-warningx"
          >
            <OverlayTrigger
              delay={{ show: 200, hide: 0 }}
              placement="top"
              overlay={<Tooltip id="tooltip-top">El proveedor ya reembolsó a Autopaquete.</Tooltip>}
            >
              <div className="text-muted text-small" ref={canceledRef}>
                Cancelada
              </div>
            </OverlayTrigger>
            <input
              type="checkbox"
              className="form-check-input cursor-pointer"
              name="receiver"
              checked={canceled}
              onClick={() => {
                setIsModalStatesActive({ isOpen: true, state: 'canceled' });
              }}
            />
          </Col>
          <Col
            xs="6"
            sm="auto"
            xxl="1"
            className="ps-sm-4 mb-2 mb-xxl-0 d-flex flex-column justify-content-center align-items-endx text-endx order-5 cursor-pointer bg-infox"
          >
            <OverlayTrigger
              delay={{ show: 200, hide: 0 }}
              placement="top"
              overlay={<Tooltip id="tooltip-top">El costo de la guía ya fue devuelto al cliente.</Tooltip>}
            >
              <div className="text-muted text-small" ref={refundedRef}>
                Reembolsada
              </div>
            </OverlayTrigger>
            <input
              type="checkbox"
              className="form-check-input cursor-pointer"
              name="receiver"
              checked={refunded}
              onClick={() => {
                if (refunded) {
                  ToastErrorCath('Este envío ya fue reembolsado.');
                } else {
                  setIsModalActive(true);
                }
              }}
            />
          </Col>
          <Col
            xs="6"
            sm="auto"
            xxl="1"
            className={`ps-4 mb-2 mb-xxl-0 d-flex flex-column justify-content-center align-items-endx text-endx order-5 cursor-pointer bg-secondaryx ${
              data?.refunded ? 'visible' : 'invisible'
            }`}
          >
            <OverlayTrigger
              delay={{ show: 200, hide: 0 }}
              placement="top"
              overlay={<Tooltip id="tooltip-top">Esta guía fue usada posteriormente al reembolso.</Tooltip>}
            >
              <div className="text-muted text-small" ref={usedRef}>
                Utilizada
              </div>
            </OverlayTrigger>
            <input
              type="checkbox"
              className="form-check-input cursor-pointer"
              name="receiver"
              checked={consumed}
              onClick={() => {
                if (consumed) {
                  ToastErrorCath('no se puede volver a cobrar.');
                } else {
                  setIsModalActive(true);
                }
              }}
            />
          </Col>
          <Col
            xs="12"
            // sm="auto"
            xxl="1"
            className={`ps-4 mb-2 mt-4 mt-sm-0 mb-xxl-0 d-flex flex-column justify-content-center align-items-end order-5 cursor-pointer bg-alternatex ${
              data?.refunded || data?.consumed ? 'invisible' : 'visible'
            }`}
          >
            <OverlayTrigger
              delay={{ show: 200, hide: 0 }}
              placement="top"
              overlay={<Tooltip id="tooltip-top">La guía fue utilizada por el cliente previamente.</Tooltip>}
            >
              <div className="text-muted text-small" ref={notApplyRef}>
                No aplica
              </div>
            </OverlayTrigger>
            <input
              type="checkbox"
              className="form-check-input cursor-pointer"
              name="receiver"
              checked={notApply}
              onClick={() => {
                setIsModalStatesActive({ isOpen: true, state: 'not_apply' });
              }}
            />
          </Col>
          <Col xs="12" sm="2" xxl="1" className="d-flex justify-content-end align-items-center order-1 order-sm-2 order-xxl-last bg-infox">
            <div className="bg-secondaryx text-end">
              <div className="text-muted text-small d-xxl-none">Detalles</div>
              <OverlayTrigger
                key={isDetailModalActive ? 'open' : 'closed'}
                delay={{ show: 200, hide: 0 }}
                placement="top"
                overlay={<Tooltip id="tooltip-top">Ver detalles</Tooltip>}
              >
                <button
                  type="button"
                  className="btn btn-link p-0 sh-3 text-muted"
                  onClick={() => {
                    setIsDetailModalActive(true);
                  }}
                >
                  <CsLineIcons icon="file-text" />
                </button>
              </OverlayTrigger>
            </div>
          </Col>
        </Row>
      </Card>
    </>
  );
};
export default ShippingCard;
